import { useMemo, useState } from 'react'
import type { GarmentCard, SessionCommand } from '@fitting-room/contracts'
import { StepShell } from '../../components/RoomChrome'
import { GarmentCardFace } from '../../components/GarmentCardFace'
import { uiStrings } from '../../lib/strings'
import { text } from '../../lib/text'
import { useGarmentsByIds } from '../useSession'
import type { StepProps } from '../types'

/**
 * Tap to KEEP.
 *
 * The copy never says "remove". What happens to the ones not tapped is
 * decided by the step document (`overflowToSlot` / `overflowToPool`) and by the
 * server — nothing is deleted, and the muted treatment is there to say
 * "these move on somewhere else", not "these are gone".
 *
 * How many are kept is `slotStates[].capacity`, and when the slot holds fewer
 * picks than its capacity there is nothing to cut: everything is pre-kept and
 * the muting never appears.
 */
export function ShortlistStep({ step, ctx }: StepProps<'shortlist'>) {
  const { view, locale, token } = ctx
  const t = uiStrings(locale)

  const slot = view.flow.slots.find((s) => s.key === step.slotKey)
  const state = view.slotStates.find((s) => s.key === step.slotKey)
  const capacity = state?.capacity ?? 0

  const picks = useMemo(
    () => view.session.picks.filter((p) => p.slotKey === step.slotKey).sort((a, b) => a.position - b.position),
    [view.session.picks, step.slotKey],
  )

  const expected = Math.min(capacity, picks.length)
  const mustCut = picks.length > capacity

  const [kept, setKept] = useState<readonly string[]>(() => (mustCut ? [] : picks.map((p) => p.garmentId)))

  const ids = useMemo(() => picks.map((p) => p.garmentId), [picks])
  const garments = useGarmentsByIds(token, ids)
  const byId = useMemo(() => {
    const map = new Map<string, GarmentCard>()
    for (const item of garments.data?.items ?? []) map.set(item.garment.id, item)
    return map
  }, [garments.data])

  const full = kept.length >= expected
  const toggle = (garmentId: string) => {
    setKept((current) => {
      if (current.includes(garmentId)) return current.filter((id) => id !== garmentId)
      if (current.length >= expected) return current
      return [...current, garmentId]
    })
  }

  const command: SessionCommand = { type: 'keep', slotKey: step.slotKey, garmentIds: [...kept] }

  if (picks.length === 0) {
    return (
      <StepShell
        title={text(step.title, locale) || (slot ? text(slot.label, locale) : step.slotKey)}
        body={text(step.body, locale)}
        footer={
          <button type="button" className="fr-btn fr-btn-primary w-full" disabled={ctx.pending} onClick={() => ctx.advance()}>
            {text(step.ctaLabel, locale) || t.continue}
          </button>
        }
      >
        <p className="text-sm" style={{ color: 'var(--fr-ink-dim)' }}>
          {t.nothingToShortlist}
        </p>
      </StepShell>
    )
  }

  return (
    <StepShell
      wide
      title={text(step.title, locale) || (slot ? text(slot.label, locale) : step.slotKey)}
      body={text(step.body, locale)}
      footer={
        <div className="pb-2" style={{ background: 'linear-gradient(to top, var(--fr-ground-deep) 62%, transparent)' }}>
          <p className="fr-eyebrow mb-3 text-center" aria-live="polite">
            {t.keptOf(kept.length, expected)}
          </p>
          <button
            type="button"
            className="fr-btn fr-btn-primary w-full"
            disabled={ctx.pending || kept.length !== expected}
            onClick={() => ctx.advance([command])}
          >
            {text(step.ctaLabel, locale) || t.keepN(expected)}
          </button>
        </div>
      }
    >
      <ul className="grid grid-cols-2 gap-3">
        {picks.map((pick) => {
          const card = byId.get(pick.garmentId)
          const isKept = kept.includes(pick.garmentId)
          const muted = full && !isKept
          return (
            <li key={pick.garmentId}>
              <button
                type="button"
                className="relative block w-full text-left transition-transform duration-300"
                aria-pressed={isKept}
                onClick={() => toggle(pick.garmentId)}
                style={{
                  transform: isKept ? 'translateY(-4px)' : 'none',
                  outline: isKept ? '1px solid var(--fr-primary)' : '1px solid transparent',
                  outlineOffset: 2,
                }}
              >
                <span className={`relative block ${muted ? 'fr-muted fr-muted-stripes' : ''}`}>
                  {card ? (
                    <GarmentCardFace card={card} attributes={view.tenant.attributes} locale={locale} compact />
                  ) : (
                    <span className="fr-card-face flex h-40 items-center justify-center p-4 text-center">
                      {/* Never the id: `rck_02c820d1…` is not a thing she chose. */}
                      <span className="fr-eyebrow">{t.gownUnavailable}</span>
                    </span>
                  )}
                </span>
                {isKept ? (
                  <span
                    className="absolute left-0 top-0 h-1 w-full"
                    style={{ background: 'var(--fr-primary)' }}
                    aria-hidden="true"
                  />
                ) : null}
              </button>
              {pick.note ? (
                <p className="mt-1 text-[0.75rem]" style={{ color: 'var(--fr-ink-faint)' }}>
                  {pick.note}
                </p>
              ) : null}
            </li>
          )
        })}
      </ul>
    </StepShell>
  )
}
